import { Close } from '@mui/icons-material';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import {
    Alert,
    Box,
    Button,
    Chip,
    Container,
    darken,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Grid,
    lighten,
    Paper,
    Stack,
    Tab,
    Tabs,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { zodResolver } from '@hookform/resolvers/zod';
import { isEqual } from 'date-fns';
import moment from 'moment';
import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { FieldValues, FormProvider, useForm, useWatch } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import {
    useBeforeUnload,
    useBlocker,
    useLocation,
    useNavigate,
} from 'react-router-dom';
import { z } from 'zod';
import { setBookerObject } from '../../../../app/Slices/BookerSlice';
import {
    useCalculateFeesMutation,
    useCancelJobMutation,
    useCreateJobRequestMutation,
    useEditJobMutation,
    useEditReservationMutation,
    useGetAllGuestAttendeesQuery,
    useGetAvailabilityQuery,
    useGetBookingCapAvailabilityMutation,
    useGetIndividualAssetQuery,
    useGetIndividualClientQuery,
    useGetJobsQuery,
    useGetProfileCompanyInfoQuery,
    useGetProfileDetailsQuery,
} from '../../../../app/Slices/SevenDayApiSlice';
import { CustomButton } from '../../../../layout/styledcomponents/CustomButton';
import LoadingComponent from '../../../../layout/styledcomponents/LoadingAnimations/LoadingComponent';
import UserRole from '../../../../shared/AuthRoles';
import {
    camelCaseToTitleCase,
    handleHttpError,
} from '../../../../shared/utils';
import { ServiceType } from '../../../enums/contractor';
import { ExperienceType } from '../../../enums/experience';
import { GuestRole } from '../../../enums/users';
import toast from '../../../shared/Toast';
import CancelModal from '../../Experiences/Owner/CancelModal';
import { TimeArray } from '../../Experiences/Owner/Property/TimeArray';
import CalendarModal from './CalendarModal';
import ReservationEditDetailsSection from './ReservationEditDetailsSection';
import ReservationHeader from './ReservationHeader';
import {
    addHoursHHmm,
    buildOptionalServiceExpenseLines,
    collectRhfErrorMessages,
    formatDMY,
    formatYMD,
    getReservationDateDisplay,
    isApprovedReservationCurrentlyActive,
    contractorJobIdsToCancel,
    mergeJobsFormWithServer,
    setMenusToSendOnReservationUpdate,
    subHoursHHmm,
} from './reservationEdit.utils';
import { getRequestSchema } from '../ReservationRequest/Shared/schema';
import { shouldValidateSameDayHireHours } from '../ReservationRequest/Shared/reservationUtils';
import ReservationSummaryBlock from './ReservationSummaryBlock';
import ReservationChargesCard from './ReservationChargesCard';
import { BookingSetMenu } from '../../../types/set-menu';
import { shouldShowOptionalServicesSection } from './optionalServiceAvailability';
import {
    Job,
    JobCreatedBy,
    ProviderType,
} from '../../../../app/Slices/SevenDayApiTypes';
import { OWNER_SET_MENU_CONTRACTOR_ID_CATERING } from '../ReservationRequest/ReservationJobs/reservationJobs.helpers';

type EditNavKey = 'details' | 'services' | 'notes' | 'issues';

export default function EditReservationForm(params: any) {
    const loggedInUser = UserRole();
    const isOwner = loggedInUser?.role?.includes('owner');
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch();
    const isItineraryItemForm = params?.isItineraryItemForm;
    const submitRef = params?.submitRef;
    const onReservationSuccess = params?.onReservationSuccess;
    const theme = useTheme();

    const [reservationDetails, setReservationDetails] = useState(
        params?.reservation,
    );
    const [conflictError, setConflictError] = useState('');
    const [showSkeleton, setShowSkeleton] = useState(false);
    const [fees, setFees] = useState<any>();
    const [isFirstRender, setIsFirstRender] = useState(true);
    const [open, setOpen] = useState(false);
    const [navigationBlockerOpen, setNavigationBlockerOpen] = useState(false);
    const [bookingCapError, setBookingCapError] = useState(false);
    const [errorMsg, setErrorMsg] = useState<any>(undefined);
    const [checkinFrom, setCheckinFrom] = useState('');
    const [checkoutBefore, setCheckoutBefore] = useState('');
    const [shouldCancelServices, setShouldCancelServices] = useState(false);
    const isSubmittingRef = useRef(false);
    const initialOverallTotalRef = useRef<number | null>(null);
    const [confirmAmountOpen, setConfirmAmountOpen] = useState(false);
    const pendingSubmitRef = useRef<FormValues | null>(null);
    const bypassConfirmRef = useRef(false);
    const lastDispatchedBookerIdRef = useRef<number | string | null>(null);
    const jobsHydratedRef = useRef(false);
    const emptyArrayRef = useRef<any[]>([]);

    const reservationId = reservationDetails?.id;
    const moduleId = reservationDetails?.module?.id;
    const isOnBehalf = reservationDetails?.onBehalf;

    const {
        data: jobs,
        isLoading: isLoadingJobs,
        isFetching: isFetchingJobs,
    } = useGetJobsQuery({ bookingId: reservationId }, { skip: !reservationId });

    const { data: getIndividualAsset, isFetching } = useGetIndividualAssetQuery(
        `modules/${moduleId}?bookingId=${reservationId}`,
        { skip: !moduleId || !reservationId },
    );

    const isApprovedActiveForEditSchema = useMemo(
        () =>
            isApprovedReservationCurrentlyActive(
                reservationDetails?.status,
                reservationDetails?.startDate,
                reservationDetails?.endDate,
            ),
        [
            reservationDetails?.status,
            reservationDetails?.startDate,
            reservationDetails?.endDate,
        ],
    );

    const Schema = getRequestSchema(
        getIndividualAsset,
        reservationDetails?.bookerType,
        {
            attendeesOnlyActiveBooking: isApprovedActiveForEditSchema,
        },
    );
    type FormValues = z.infer<typeof Schema>;

    const methods = useForm<FormValues>({
        mode: 'all',
        reValidateMode: 'onChange',
        defaultValues: {
            ...(reservationDetails as any),
            policy: undefined as any,
        },
        resolver: zodResolver(Schema),
    });

    const {
        control,
        handleSubmit,
        getValues,
        setValue,
        trigger,
        formState: { isDirty, dirtyFields, isSubmitting, errors: formErrors },
        watch,
    } = methods;

    const [updateJob] = useEditJobMutation();
    const [createJob] = useCreateJobRequestMutation();
    const [cancelJob] = useCancelJobMutation();
    const [editReservation] = useEditReservationMutation();

    const watchedJobs = useWatch({ name: 'jobs' as any, control });

    const jobsArray =
        watchedJobs && Object.keys(watchedJobs).length > 0
            ? (Object.values(watchedJobs) as any[])
            : emptyArrayRef.current;

    const [calculateFees, { isLoading: isCalculating }] =
        useCalculateFeesMutation();
    const [getBookingCapAvailability] = useGetBookingCapAvailabilityMutation();

    const blocker = useBlocker(
        ({ currentLocation, nextLocation }) =>
            isDirty &&
            !isSubmitting &&
            !isSubmittingRef.current &&
            currentLocation.pathname !== nextLocation.pathname,
    );

    useBeforeUnload(
        React.useCallback(
            (event) => {
                if (isDirty && !isSubmitting && !isSubmittingRef.current) {
                    event.preventDefault();
                    return (event.returnValue = '');
                }
            },
            [isDirty, isSubmitting],
        ),
    );

    const { data: guestUsers, isFetching: isFetchingGuestUsers } =
        useGetIndividualClientQuery(`/guests/${loggedInUser?.company}/users`, {
            skip:
                loggedInUser?.role !== GuestRole.Admin &&
                loggedInUser?.role !== GuestRole.DepartmentAdmin,
        });

    useGetAllGuestAttendeesQuery(loggedInUser?.company, {
        skip: !loggedInUser?.role.includes('guest'),
    });

    const { data: profileDetails } = useGetProfileDetailsQuery('/profile');

    const { data: getCompanyDetails } = useGetProfileCompanyInfoQuery(
        `/owners/${loggedInUser?.company}`,
    );

    const isIpad = useMediaQuery('(max-width: 1200px)');
    const isMobile = useMediaQuery('(max-width: 770px)');
    const isLaptop = useMediaQuery('(max-width: 1200px)');
    const [spaceError, setSpaceError] = useState(false);

    const startDate = useWatch({ name: 'startDate', control });
    const endDate = useWatch({ name: 'endDate', control });
    const adultCount = useWatch({ name: 'bookingInfo.adults', control }) ?? 0;
    const childCount = useWatch({ name: 'bookingInfo.children', control }) ?? 0;
    const guestCount = useWatch({ name: 'bookingInfo.guests', control }) ?? 0;
    const overnightGuestCount =
        useWatch({ name: 'bookingInfo.overnightGuests', control }) ?? 0;
    const checkinTime =
        useWatch({ name: 'bookingInfo.checkinTime', control }) ?? '';
    const checkoutTime =
        useWatch({ name: 'bookingInfo.checkoutTime', control }) ?? '';
    const visitType = useWatch({ name: 'bookingInfo.visitType', control });
    const additionalNotes =
        useWatch({ name: 'bookingInfo.notes', control }) ?? '';
    const bookerId = useWatch({ name: 'booker.id', control });
    const spaceType = useWatch({ name: 'bookingInfo.spaceType', control });
    const isMeetingRoom = watch('bookingInfo.reqMeetingRoom');
    const watchedSpaces = useWatch({ name: 'spaces', control });
    const spaces = watchedSpaces ?? emptyArrayRef.current;
    const requiresCatering = useWatch({
        name: 'bookingInfo.requiresCatering',
        control,
    });

    const requiresMassages = useWatch({
        name: 'bookingInfo.requiresMassages',
        control,
    });
    const requiresBeverages = useWatch({
        name: 'bookingInfo.requiresBeverages',
        control,
    });
    const formJobs = watchedJobs;

    const cateringDetails =
        useWatch({ name: 'bookingInfo.cateringNotes', control }) ?? '';
    const beverageDetails =
        useWatch({ name: 'bookingInfo.beverageNotes', control }) ?? '';
    const massageDetails =
        useWatch({ name: 'bookingInfo.massageNotes', control }) ?? '';
    const customServices = useWatch({ name: 'customServices', control }) ?? [];

    const optionalServiceJobsKey = useMemo(() => {
        const enc = (b: unknown) => {
            if (!b || typeof b !== 'object') return '';
            return Object.values(b as Record<string, any>)
                .map(
                    (j) =>
                        `${j?.status ?? ''}:${String(j?.guestNotes ?? '').length}:${j?.items?.length ?? 0}`,
                )
                .join(',');
        };
        return `${enc((formJobs as any)?.catering)}|${enc((formJobs as any)?.wellness)}|${enc((formJobs as any)?.alcohol)}|${enc((formJobs as any)?.workshop)}|${enc((formJobs as any)?.other)}`;
    }, [formJobs]);

    const bookerType = reservationDetails?.bookerType;
    const moduleType = reservationDetails?.bookingInfo?.moduleType;

    const { data: availability } = useGetAvailabilityQuery(
        {
            url: `/modules/commercial/${getIndividualAsset?.id}/get-availability`,
            body: {
                startDate: formatYMD(startDate),
                endDate: formatYMD(endDate),
                bookingId: reservationDetails?.id,
                bookerId,
            },
        },
        { skip: getIndividualAsset?.type !== ExperienceType.Commercial },
    );

    const readableStartDate = formatDMY(startDate);
    const readableEndDate = formatDMY(endDate);
    const singleDate = formatDMY(startDate);
    const createdAtDate = formatDMY(reservationDetails?.createdAt);
    const experienceName =
        reservationDetails?.module?.name || getIndividualAsset?.name || '';
    const assetImageSrc = getIndividualAsset?.photos?.[0]?.src;
    const isModuleAvailable = reservationDetails?.module?.isAvailable;

    const checkAvailability = useCallback(
        (value: any) => {
            if (!reservationDetails?.module?.id || !reservationDetails?.id) {
                return;
            }

            const request =
                typeof value === 'number'
                    ? {
                          moduleId: reservationDetails?.module?.id,
                          startDate: formatYMD(startDate),
                          bookerId: value,
                          bookingId: reservationDetails?.id,
                      }
                    : {
                          moduleId: reservationDetails?.module?.id,
                          startDate: formatYMD(value),
                          bookerId,
                          bookingId: reservationDetails?.id,
                      };

            getBookingCapAvailability(request).then((data: any) => {
                if (data?.error?.status === 400) {
                    setBookingCapError(true);
                    setErrorMsg(data?.error?.data?.message);
                } else {
                    setBookingCapError(false);
                    setErrorMsg(undefined);
                }
            });
        },
        [
            bookerId,
            getBookingCapAvailability,
            reservationDetails?.id,
            reservationDetails?.module?.id,
            startDate,
        ],
    );

    const getExperienceOwnedByLoggedInUser = useCallback(() => {
        if (loggedInUser?.role?.includes('owner')) {
            return reservationDetails?.owner?.name === getCompanyDetails?.name;
        }
    }, [
        getCompanyDetails?.name,
        loggedInUser?.role,
        reservationDetails?.owner?.name,
    ]);

    const onClickOpenModal = useCallback(() => setOpen(true), []);
    const handleCloseModal = useCallback(() => setOpen(false), []);

    const onClickCancelEditing = useCallback(() => {
        navigate(-1);
    }, [navigate]);

    const onSubmitDate = useCallback(
        (data: FieldValues) => {
            setValue('spaces' as any, [], { shouldDirty: true });
            setOpen(false);

            setValue('startDate' as any, data.date?.from, {
                shouldValidate: true,
                shouldDirty: true,
            });

            checkAvailability(data.date?.from);

            setValue('endDate' as any, data.date?.to, { shouldDirty: true });

            setCheckinFrom(data?.checkin || '');
            if (data?.checkin)
                setValue('bookingInfo.checkinTime' as any, data?.checkin);

            setCheckoutBefore(data?.checkout || '');
            if (data?.checkout)
                setValue('bookingInfo.checkoutTime' as any, data?.checkout);
        },
        [checkAvailability, setValue],
    );

    const handleProceedNavigation = useCallback(() => {
        setNavigationBlockerOpen(false);
        blocker.proceed?.();
    }, [blocker]);

    const handleCancelNavigation = useCallback(() => {
        setNavigationBlockerOpen(false);
        blocker.reset?.();
    }, [blocker]);

    useEffect(() => {
        if (blocker.state !== 'blocked') {
            setNavigationBlockerOpen(false);
            return;
        }
        const delayMs =
            typeof theme.transitions?.duration?.leavingScreen === 'number'
                ? theme.transitions.duration.leavingScreen + 50
                : 250;
        const t = window.setTimeout(
            () => setNavigationBlockerOpen(true),
            delayMs,
        );
        return () => clearTimeout(t);
    }, [blocker.state, theme.transitions?.duration?.leavingScreen]);

    useEffect(() => {
        let timer: any;
        if (isCalculating) {
            setShowSkeleton(true);
            timer = setTimeout(() => setShowSkeleton(false), 500);
        } else {
            timer = setTimeout(() => setShowSkeleton(false), 500);
        }
        return () => clearTimeout(timer);
    }, [isCalculating]);

    useEffect(() => {
        const timer = setTimeout(() => setIsFirstRender(false), 500);
        return () => clearTimeout(timer);
    }, []);

    useEffect(() => {
        if (reservationDetails === undefined) {
            navigate(`/permissionDenied`, {
                state: { name: 'editReservation' },
            });
        }
    }, [navigate, reservationDetails]);

    useEffect(() => {
        if (getIndividualAsset?.type !== ExperienceType.Commercial) return;
        if (!reservationDetails?.bookingInfo) return;

        const currentSpaceType = methods.getValues('bookingInfo.spaceType');
        const currentReqMeetingRoom = methods.getValues(
            'bookingInfo.reqMeetingRoom',
        );

        const initialSpaceType = reservationDetails?.bookingInfo?.entireVenue
            ? 'entireVenue'
            : reservationDetails?.bookingInfo?.desks
              ? 'desk'
              : reservationDetails?.bookingInfo?.privateOffices
                ? 'privateOffice'
                : 'meetingRoom';

        const initialReqMeetingRoom =
            !!reservationDetails?.bookingInfo?.meetingRooms;

        if (!currentSpaceType) {
            setValue('bookingInfo.spaceType', initialSpaceType, {
                shouldDirty: false,
            });
        }

        if (currentReqMeetingRoom === undefined) {
            setValue('bookingInfo.reqMeetingRoom', initialReqMeetingRoom, {
                shouldDirty: false,
            });
        }
    }, [
        getIndividualAsset?.type,
        reservationDetails?.bookingInfo,
        setValue,
        methods,
    ]);

    useEffect(() => {
        if (
            startDate === endDate &&
            getIndividualAsset?.type === ExperienceType.Property
        ) {
            setValue('bookingInfo.overnightGuests', undefined as any);
        }
    }, [startDate, endDate, getIndividualAsset?.type, setValue]);

    useEffect(() => {
        if (params?.reservation && reservationDetails !== params.reservation) {
            setReservationDetails(params.reservation);
            jobsHydratedRef.current = false;
        }
    }, [params?.reservation, reservationDetails]);

    const isGuest = loggedInUser?.role?.includes('guest');
    const contractorJobs = (jobs ?? []).filter(
        (job: { serviceType?: string }) =>
            !isGuest || job.serviceType !== ServiceType.Cleaning,
    );

    const modifiedJobs = contractorJobs.map((job: any) => ({
        ...job,
        templateId: job?.items?.[0]?.template?.id,
    }));

    const toObjectById = (arr: any[]) =>
        arr.reduce((acc: Record<string, any>, job: any, index: number) => {
            const key = job?.id ?? index;
            acc[key] = job;
            return acc;
        }, {});

    const allContractorJobs = toObjectById(modifiedJobs);

    const allSetMenus: BookingSetMenu[] = reservationDetails.setMenus;
    const allSetMenusAsJobs = toObjectById(
        allSetMenus.map((bookingSetMenu: BookingSetMenu) => {
            return {
                ...bookingSetMenu,
                providerType: ProviderType.Owner,
                contractor: {
                    id: OWNER_SET_MENU_CONTRACTOR_ID_CATERING,
                },
                items: [],
            };
        }),
    );

    const cateringJobs = { ...allContractorJobs, ...allSetMenusAsJobs };

    useEffect(() => {
        if (isLoadingJobs || isFetchingJobs) return;
        if (jobsHydratedRef.current) return;

        const allJobs = [
            ...contractorJobs,
            ...(reservationDetails?.setMenus ?? []),
        ];
        if (allJobs.length === 0) return;

        const currentJobs = getValues('jobs' as any) ?? {};

        setValue(
            'jobs' as any,
            mergeJobsFormWithServer(cateringJobs, currentJobs) as any,
            { shouldDirty: false, shouldTouch: false, shouldValidate: false },
        );

        jobsHydratedRef.current = true;
    }, [jobs, getValues, setValue]);

    useEffect(() => {
        if (!startDate) return;
        checkAvailability(startDate);
    }, [startDate, checkAvailability]);

    const checkinT = getIndividualAsset?.checkinTime;
    const checkoutT = getIndividualAsset?.checkoutTime;
    const min = getIndividualAsset?.minHireHours;
    const max = getIndividualAsset?.maxHireHours;

    const timeToAdd = moment(min, 'HH').format('HH:mm');
    const timeToSubtract = moment(max, 'HH').format('HH:mm');

    const compareCheckinSettings = useMemo(
        () =>
            formatYMD(startDate) === formatYMD(endDate) && checkoutT > checkinT
                ? TimeArray?.filter((element: any) => {
                      if (
                          getIndividualAsset?.type === 'activity' ||
                          getIndividualAsset?.type === 'boat'
                      ) {
                          if (max >= 24) {
                              return (
                                  element.value >= checkinT &&
                                  element.value <= checkoutT &&
                                  element.value >= checkinFrom
                              );
                          }

                          return (
                              element.value >= checkinT &&
                              element.value <= checkoutT &&
                              element.value <=
                                  subHoursHHmm(checkoutT, timeToAdd) &&
                              element.value >= checkinFrom
                          );
                      }

                      return (
                          element.value >= checkinT &&
                          element.value >= checkinFrom
                      );
                  })
                : TimeArray?.filter(
                      (element: any) =>
                          element.value >= checkinT &&
                          element.value >= checkinFrom,
                  ),
        [
            checkinFrom,
            checkinT,
            checkoutT,
            endDate,
            getIndividualAsset?.type,
            max,
            startDate,
            timeToAdd,
        ],
    );

    const compareCheckoutSettings = useMemo(
        () =>
            formatYMD(startDate) === formatYMD(endDate)
                ? TimeArray?.filter((element: any) => {
                      const checkinTimeSelected =
                          checkinTime === '' ? checkinT : checkinTime;

                      if (
                          getIndividualAsset?.type === 'activity' ||
                          getIndividualAsset?.type === 'boat'
                      ) {
                          if (max >= 24) {
                              return (
                                  element.value > checkinTimeSelected &&
                                  element.value <= checkoutT &&
                                  (!checkoutBefore ||
                                      element.value <= checkoutBefore)
                              );
                          }

                          return (
                              element.value > checkinTimeSelected &&
                              element.value <= checkoutT &&
                              (!checkoutBefore ||
                                  element.value <= checkoutBefore) &&
                              element.value >=
                                  addHoursHHmm(
                                      checkinTime ? checkinTime : checkinT,
                                      timeToAdd,
                                  )
                          );
                      }

                      return (
                          element.value > checkinTimeSelected &&
                          element.value <= checkoutT &&
                          (!checkoutBefore || element.value <= checkoutBefore)
                      );
                  })
                : TimeArray?.filter(
                      (element: any) => element.value <= checkoutT,
                  ),
        [
            checkinT,
            checkinTime,
            checkoutBefore,
            checkoutT,
            endDate,
            getIndividualAsset?.type,
            max,
            startDate,
            timeToAdd,
        ],
    );

    useEffect(() => {
        if (bookerId !== undefined && bookerId !== null) {
            checkAvailability(bookerId);
        }

        const obj = guestUsers?.find((o: any) => o.id === bookerId);

        if (obj?.id != null && lastDispatchedBookerIdRef.current !== obj.id) {
            dispatch(setBookerObject(obj));
            lastDispatchedBookerIdRef.current = obj.id;
        }

        if (
            obj &&
            !obj.profilePicture &&
            (getIndividualAsset?.usesNirovision ||
                getIndividualAsset?.details?.dropoffUsesNirovision)
        ) {
            toast.warning(
                `This experience uses facial recognition. The selected user does not have a profile picture, which may affect the check-in process.`,
                { duration: Infinity, customId: 'faceRecWarning' },
            );
        } else {
            toast.remove('faceRecWarning');
        }
    }, [bookerId, checkAvailability, dispatch, getIndividualAsset, guestUsers]);

    useEffect(() => {
        if (
            getIndividualAsset?.type === 'vehicle' &&
            (isOnBehalf || bookerType !== 'owner')
        ) {
            if (loggedInUser.role.includes('admin') && !isFetchingGuestUsers) {
                dispatch(
                    setBookerObject(
                        guestUsers?.find(
                            (user: any) =>
                                user.id === reservationDetails?.booker?.id,
                        ),
                    ),
                );
            } else {
                dispatch(setBookerObject(profileDetails));
            }
        }
    }, [
        bookerType,
        dispatch,
        getIndividualAsset?.type,
        guestUsers,
        isFetchingGuestUsers,
        isOnBehalf,
        loggedInUser.role,
        profileDetails,
        reservationDetails?.booker?.id,
    ]);

    useEffect(() => {
        setConflictError('');

        if (
            (availability?.desks < getIndividualAsset?.details?.desks ||
                availability?.privateOffices <
                    getIndividualAsset?.details?.privateOffices ||
                availability?.meetingRooms <
                    getIndividualAsset?.details?.meetingRooms) &&
            loggedInUser?.role?.includes('owner') &&
            !isOnBehalf &&
            bookerType === 'owner'
        ) {
            setConflictError(
                `You cannot block the selected period. There are existing bookings during this timeframe. Please choose a different period.`,
            );
        } else if (
            availability &&
            !availability?.entireVenueAvailable &&
            spaceType === 'entireVenue'
        ) {
            setConflictError(
                `You cannot book the entire venue. There are existing bookings during this timeframe. Please choose a different period.`,
            );
        } else if (
            availability?.conflictingBookingIds?.length > 0 &&
            (loggedInUser?.role === 'guest_admin' ||
                loggedInUser?.role === 'guest_department_admin')
        ) {
            setConflictError(
                `You already have a booking for the selected dates. Please change the dates or the booker.`,
            );
        } else if (
            availability?.conflictingBookingIds?.length > 0 &&
            !loggedInUser?.role.includes('owner')
        ) {
            setConflictError(
                `You already have a booking for the selected dates. Please change the dates`,
            );
        }
    }, [
        availability,
        bookerType,
        getIndividualAsset?.details,
        isOnBehalf,
        loggedInUser?.role,
        spaceType,
    ]);

    const commercialAttendees =
        getIndividualAsset?.type === ExperienceType.Commercial &&
        (spaceType === 'meetingRoom' ||
            spaceType === 'entireVenue' ||
            isMeetingRoom);

    const mustCompleteMeetingRoomDetails =
        getIndividualAsset?.type === ExperienceType.Commercial &&
        isMeetingRoom &&
        spaceType !== 'meetingRoom' &&
        spaceType !== 'entireVenue';

    const disableSaveOffice =
        ((availability?.desks < 1 && spaceType === 'desk') ||
            (availability?.privateOffices < 1 &&
                spaceType === 'privateOffice') ||
            (availability?.meetingRooms < 1 && spaceType === 'meetingRoom') ||
            (availability?.meetingRooms < 1 && isMeetingRoom) ||
            !!conflictError) &&
        getIndividualAsset?.type === 'commercial';

    useEffect(() => {
        if (getIndividualAsset?.type !== ExperienceType.Commercial) return;

        const isOwnerBlocked =
            reservationDetails?.bookerType === 'owner' &&
            reservationDetails?.onBehalf === false;

        const guests = methods.getValues('bookingInfo.guests');
        const staff = methods.getValues('staffAttending') ?? [];
        const external = methods.getValues('externalAttendees') ?? [];
        const reqMeetingRoom = methods.getValues('bookingInfo.reqMeetingRoom');

        if (!commercialAttendees && isDirty && !isOwnerBlocked) {
            if (guests !== 0) {
                setValue('bookingInfo.guests' as any, 0 as any, {
                    shouldDirty: true,
                    shouldTouch: false,
                    shouldValidate: false,
                });
            }

            if (staff.length > 0) {
                setValue('staffAttending' as any, [] as any, {
                    shouldDirty: true,
                    shouldTouch: false,
                    shouldValidate: false,
                });
            }

            if (external.length > 0) {
                setValue('externalAttendees' as any, [] as any, {
                    shouldDirty: true,
                    shouldTouch: false,
                    shouldValidate: false,
                });
            }
        }

        if (
            (spaceType === 'entireVenue' || spaceType === 'meetingRoom') &&
            reqMeetingRoom !== false
        ) {
            setValue('bookingInfo.reqMeetingRoom' as any, false as any, {
                shouldDirty: true,
                shouldTouch: false,
                shouldValidate: false,
            });
        }
    }, [
        commercialAttendees,
        getIndividualAsset?.type,
        isDirty,
        methods,
        reservationDetails?.bookerType,
        reservationDetails?.onBehalf,
        setValue,
        spaceType,
    ]);

    useEffect(() => {
        const isOwnerBlocked =
            reservationDetails?.bookerType === 'owner' &&
            reservationDetails?.onBehalf === false;

        if (isOwnerBlocked) {
            return;
        }

        calculateFees({
            ...(control as any)._formValues,
            startDate: formatYMD(startDate),
            endDate: formatYMD(endDate),
            module: { id: reservationDetails?.module?.id },
            policies: reservationDetails?.policies,
            staffAttending: [],
            externalAttendees: [],
            spaces,
            requiresServices: false,
            bookingInfo: {
                moduleType,
                visitType,
                checkinTime: checkinTime || '08:00',
                checkoutTime: checkoutTime || '17:00',
                adults: adultCount,
                children: childCount,
                overnightGuests: overnightGuestCount,
                requiresCatering: !!requiresCatering,
                cateringNotes: cateringDetails,
                requiresBeverages: !!requiresBeverages,
                beverageNotes: beverageDetails,
                requiresMassages: !!requiresMassages,
                massageNotes: massageDetails,
                notes: additionalNotes,
                desks: spaceType === 'desk' ? 1 : 0,
                privateOffices: spaceType === 'privateOffice' ? 1 : 0,
                meetingRooms:
                    spaceType === 'meetingRoom' ||
                    (isMeetingRoom && spaceType !== 'entireVenue')
                        ? 1
                        : 0,
                entireVenue: spaceType === 'entireVenue',
                guests: adultCount || guestCount,
                drivers: [],
                driverSameAsBooker: false,
            },
        }).then((data: any) => {
            if (data?.error) return;

            setFees((prev: any) => {
                const next = data?.data;
                if (JSON.stringify(prev) === JSON.stringify(next)) {
                    return prev;
                }
                return next;
            });
        });
    }, [
        additionalNotes,
        adultCount,
        beverageDetails,
        cateringDetails,
        checkinTime,
        checkoutTime,
        childCount,
        control,
        endDate,
        guestCount,
        isMeetingRoom,
        massageDetails,
        moduleType,
        overnightGuestCount,
        requiresBeverages,
        requiresCatering,
        requiresMassages,
        reservationDetails?.module?.id,
        reservationDetails?.policies,
        reservationDetails?.bookerType,
        reservationDetails?.onBehalf,
        getIndividualAsset?.type,
        spaceType,
        spaces,
        startDate,
        visitType,
        calculateFees,
    ]);

    useEffect(() => {
        const currentMassageNotes =
            methods.getValues('bookingInfo.massageNotes') ?? '';
        const currentBeverageNotes =
            methods.getValues('bookingInfo.beverageNotes') ?? '';
        const currentCateringNotes =
            methods.getValues('bookingInfo.cateringNotes') ?? '';

        if (!requiresMassages && currentMassageNotes !== '') {
            setValue('bookingInfo.massageNotes' as any, '' as any, {
                shouldDirty: true,
                shouldTouch: false,
                shouldValidate: false,
            });
        }

        if (!requiresBeverages && currentBeverageNotes !== '') {
            setValue('bookingInfo.beverageNotes' as any, '' as any, {
                shouldDirty: true,
                shouldTouch: false,
                shouldValidate: false,
            });
        }

        if (!requiresCatering && currentCateringNotes !== '') {
            setValue('bookingInfo.cateringNotes' as any, '' as any, {
                shouldDirty: true,
                shouldTouch: false,
                shouldValidate: false,
            });
        }
    }, [
        requiresMassages,
        requiresBeverages,
        requiresCatering,
        methods,
        setValue,
    ]);

    const allJobsArray: (Job | BookingSetMenu)[] = formJobs
        ? Object.values(formJobs || {})
        : [];

    const errors = useMemo(() => {
        const nextErrors: string[] = [];
        const newTimeSelection = subHoursHHmm(checkoutTime, checkinTime);
        const validateSameDayHours = shouldValidateSameDayHireHours(
            min ?? 0,
            max ?? 0,
        );

        if (
            validateSameDayHours &&
            newTimeSelection < timeToAdd &&
            formatYMD(startDate) === formatYMD(endDate) &&
            timeToSubtract !== '00:00' &&
            (isOnBehalf || bookerType !== 'owner')
        ) {
            nextErrors.push(
                `Your selection is below the allowed hire time for this experience ${min}hrs`,
            );
        }

        if (
            validateSameDayHours &&
            newTimeSelection > timeToSubtract &&
            formatYMD(startDate) === formatYMD(endDate) &&
            timeToSubtract !== '00:00'
        ) {
            nextErrors.push(
                `Your selection exceeds the allowed hire time for this experience ${min}-${max} hrs`,
            );
        }

        if (
            formatYMD(startDate) === formatYMD(endDate) &&
            checkinTime > checkoutTime
        ) {
            nextErrors.push(
                `Checkin time cannot be greater than checkout time`,
            );
        }

        return nextErrors;
    }, [
        bookerType,
        checkinTime,
        checkoutTime,
        endDate,
        isOnBehalf,
        max,
        min,
        startDate,
        timeToAdd,
        timeToSubtract,
    ]);

    const isPendingPastReservation = useMemo(() => {
        const status = String(reservationDetails?.status ?? '').toLowerCase();
        if (status !== 'pending') return false;
        const endYmd = endDate ? formatYMD(endDate) : '';
        if (!endYmd) return false;
        if (!moment(endYmd, 'YYYY-MM-DD', true).isValid()) return false;
        return moment(endYmd, 'YYYY-MM-DD').isBefore(moment(), 'day');
    }, [reservationDetails?.status, endDate]);

    const cannotSavePastPendingReservation =
        isPendingPastReservation &&
        (loggedInUser?.role?.includes('guest') ||
            loggedInUser?.role?.includes('owner'));

    const hasCalculatedCharges = (fees?.charges?.length ?? 0) > 0;
    const hasExistingCharges =
        (reservationDetails?.charges?.length ?? 0) > 0 ||
        Number(reservationDetails?.bookingTotal?.total) > 0 ||
        (reservationDetails?.expenses?.length ?? 0) > 0;

    const showCharges =
        (bookerType !== 'owner' || isOnBehalf) &&
        (hasCalculatedCharges || hasExistingCharges);

    const computeOverallTotal = useCallback(
        (values: FormValues, feesInput: any) => {
            const bookingTotal =
                Number(
                    feesInput?.bookingTotal?.total ??
                        reservationDetails?.bookingTotal?.total,
                ) || 0;
            const apiExpenses = reservationDetails?.expenses ?? [];
            const optionalExpenses = buildOptionalServiceExpenseLines(
                (values as any)?.jobs,
            );
            const merged = [...optionalExpenses, ...apiExpenses];
            const totalExpenses = merged.reduce(
                (sum: number, item: any) => sum + (Number(item?.amount) || 0),
                0,
            );
            return bookingTotal + totalExpenses;
        },
        [reservationDetails?.bookingTotal?.total, reservationDetails?.expenses],
    );

    useEffect(() => {
        if (initialOverallTotalRef.current != null) return;
        if (!reservationDetails) return;
        const currentValues = (control as any)?._formValues as FormValues;
        if (!currentValues) return;
        initialOverallTotalRef.current = computeOverallTotal(
            currentValues,
            null,
        );
    }, [computeOverallTotal, control, reservationDetails]);

    const submitToApi = async (data: FormValues) => {
        if (cannotSavePastPendingReservation) {
            toast.error(
                'This reservation is in the past and still pending. Update the dates before saving.',
            );
            return;
        }
        try {
            const jobsForPersist = allJobsArray.filter(
                (job) => job.providerType === ProviderType.Contractor,
            ) as Job[];
            const setMenuJobsArray = allJobsArray.filter(
                (job) => job.providerType === ProviderType.Owner,
            ) as BookingSetMenu[];

            let additionalBookingInfo: any = {};

            if (
                getIndividualAsset?.type === 'commercial' &&
                data?.bookingInfo
            ) {
                additionalBookingInfo = {
                    desks: data.bookingInfo.spaceType === 'desk' ? 1 : 0,
                    privateOffices:
                        data.bookingInfo.spaceType === 'privateOffice' ? 1 : 0,
                    meetingRooms:
                        data.bookingInfo.spaceType === 'meetingRoom' ||
                        (data.bookingInfo.reqMeetingRoom &&
                            data.bookingInfo.spaceType !== 'entireVenue')
                            ? 1
                            : 0,
                    entireVenue: data.bookingInfo.spaceType === 'entireVenue',
                };
            }
            const turnedOffAdditionalServices =
                Boolean(
                    (dirtyFields as { requiresServices?: boolean })
                        .requiresServices,
                ) && data.requiresServices === false;

            const serverHasOptionalServices =
                (jobs?.length ?? 0) > 0 ||
                (reservationDetails?.setMenus?.length ?? 0) > 0;
            const optionalServicesReady =
                !serverHasOptionalServices || jobsHydratedRef.current;

            const setMenus: BookingSetMenu[] | undefined = optionalServicesReady
                ? setMenusToSendOnReservationUpdate(
                      setMenuJobsArray,
                      turnedOffAdditionalServices,
                  ).map((setMenuJob) => {
                      return {
                          ...setMenuJob,
                          id:
                              typeof setMenuJob.id === 'number'
                                  ? setMenuJob.id
                                  : undefined,
                      };
                  })
                : undefined;

            const request: any = {
                ...data,
                startDate: formatYMD(startDate),
                endDate: formatYMD(endDate),
                bookerType: reservationDetails?.bookerType,
                ...(isOnBehalf || reservationDetails?.bookerType === 'guest'
                    ? {
                          bookingInfo: {
                              moduleType: getIndividualAsset?.type,
                              ...data.bookingInfo,
                              ...additionalBookingInfo,
                              overnightGuests: isEqual(startDate, endDate)
                                  ? 0
                                  : data.bookingInfo.overnightGuests,
                          },
                      }
                    : {}),
                ...(setMenus ? { setMenus } : {}),
            };

            const url = `/bookings/${reservationDetails?.id}`;
            const result = await editReservation([url, request]).unwrap();

            isSubmittingRef.current = true;

            const jobsDirtyOrNew =
                !!(dirtyFields as any).jobs ||
                jobsForPersist.some((job: any) => job?.newJob);

            if (
                !turnedOffAdditionalServices &&
                jobsForPersist.length > 0 &&
                jobsDirtyOrNew
            ) {
                const dirtyJobIds = (keys: string[]) =>
                    keys
                        .map((id) => parseInt(id, 10))
                        .filter((id) => !Number.isNaN(id));

                const updatedJobs = (dirtyFields as any)?.jobs
                    ? dirtyJobIds(Object.keys((dirtyFields as any).jobs))
                    : [];

                for (const job of jobsForPersist) {
                    if ((job as any).newJob) {
                        const payload: any = {
                            ...job,
                            booking: { id: reservationDetails?.id },
                        };
                        delete payload.newJob;
                        delete payload.contractorHasTemplates;
                        await createJob(payload).unwrap();
                    } else if (updatedJobs.includes((job as any).id)) {
                        const updateJobData: any = { ...job };
                        delete updateJobData.module;
                        delete updateJobData.recurringId;
                        await updateJob([
                            (job as any).id,
                            updateJobData,
                        ]).unwrap();
                    }
                }
            }

            if (optionalServicesReady) {
                const deletedJobIds = contractorJobIdsToCancel({
                    existingJobs: jobs ?? [],
                    jobsStillOnForm: jobsForPersist,
                    turnedOffAdditionalServices,
                });

                for (const jobId of deletedJobIds) {
                    await cancelJob(jobId).unwrap();
                }
            }

            toast.success(`Reservation has been edited`);

            if (isItineraryItemForm) {
                onReservationSuccess?.(result);
            }

            if (!isItineraryItemForm) {
                navigate(
                    `/reservations/${reservationDetails?.module?.name
                        .replace(/ /g, '')
                        .toLowerCase()}/${reservationDetails?.id}`,
                    {
                        state: {
                            landedFromEdit: true,
                            bookingMainChanges: (result as any)
                                .bookingMainChanges,
                        },
                    },
                );
            }
        } catch (error) {
            if (loggedInUser?.role.includes('guest')) {
                toast.error('Failed to edit reservation');
                return;
            }

            toast.error(handleHttpError(error));
        }
    };

    const onSubmit = async (data: FormValues) => {
        if (!bypassConfirmRef.current) {
            const initial = initialOverallTotalRef.current;
            const next = computeOverallTotal(data, fees);

            // Compare in cents to avoid float jitter.
            const initialCents =
                initial == null ? null : Math.round(initial * 100);
            const nextCents = Math.round(next * 100);

            if (initialCents != null && initialCents !== nextCents) {
                pendingSubmitRef.current = data;
                setConfirmAmountOpen(true);
                return;
            }
        }

        bypassConfirmRef.current = false;
        return submitToApi(data);
    };

    if (submitRef) {
        submitRef.current = () => handleSubmit(onSubmit)();
    }

    const dateDisplay = getReservationDateDisplay(
        reservationDetails,
        readableStartDate,
        readableEndDate,
        singleDate,
        getExperienceOwnedByLoggedInUser,
    );

    const isApprovedActiveReservation = useMemo(
        () =>
            isApprovedReservationCurrentlyActive(
                reservationDetails?.status,
                startDate,
                endDate,
            ),
        [reservationDetails?.status, startDate, endDate],
    );

    const showOptionalServicesSection = shouldShowOptionalServicesSection(
        getIndividualAsset,
        reservationDetails,
    );

    const watchedStaffAttending = useWatch({ name: 'staffAttending', control });
    const watchedExternalAttendees = useWatch({
        name: 'externalAttendees',
        control,
    });

    const staffAttending = watchedStaffAttending ?? emptyArrayRef.current;
    const externalAttendees = watchedExternalAttendees ?? emptyArrayRef.current;

    const incompleteJobs = allJobsArray.filter((job) => {
        return (
            (job.providerType === ProviderType.Owner ||
                (job.providerType === ProviderType.Contractor &&
                    job.createdBy === JobCreatedBy.Guest)) &&
            (!job.jobDate || !job.startTime || !job.endTime)
        );
    });

    const formIssues = useMemo(() => {
        const issues: string[] = [];

        if (cannotSavePastPendingReservation) {
            issues.push(
                'This reservation is in the past and still pending. Update the reservation dates before saving.',
            );
        }

        if (spaceError) {
            issues.push('Please review your selected spaces.');
        }

        if (errorMsg !== undefined) {
            issues.push(
                `${errorMsg} ${
                    loggedInUser.role === 'guest_admin' ||
                    loggedInUser.role === 'guest_department_admin'
                        ? 'Please change the dates or the primary guest.'
                        : 'Please change the dates.'
                }`,
            );
        }

        if (conflictError) {
            issues.push(conflictError);
        }

        if (
            mustCompleteMeetingRoomDetails &&
            !loggedInUser?.role?.includes('owner') &&
            staffAttending.length < 1 &&
            externalAttendees.length < 1
        ) {
            issues.push(
                'Either select one staff attending or one guest attendee for the meeting room',
            );
        }

        const ownerMadeBooking = reservationDetails?.bookerType === 'owner';

        const experienceType = getIndividualAsset?.type;
        if (
            (experienceType === ExperienceType.Property ||
                experienceType === ExperienceType.Boat) &&
            adultCount < 1 &&
            !ownerMadeBooking
        ) {
            issues.push('At least 1 guest required');
        }

        if (
            experienceType === ExperienceType.Activity &&
            guestCount < 1 &&
            !ownerMadeBooking
        ) {
            issues.push('At least 1 guest required');
        }

        if (incompleteJobs.length > 0) {
            issues.push('Missing information for job.');
        }

        const endYmd = endDate ? formatYMD(endDate) : '';
        const hasRhfValidationMessages =
            collectRhfErrorMessages(formErrors).length > 0;
        if (
            hasRhfValidationMessages &&
            endYmd &&
            moment(endYmd, 'YYYY-MM-DD', true).isValid() &&
            moment(endYmd, 'YYYY-MM-DD').isBefore(moment(), 'day') &&
            !cannotSavePastPendingReservation
        ) {
            issues.push(
                'This reservation is in the past; check service bookings and other fields that still require values.',
            );
        }

        return issues.filter(
            (issue, index, array) => array.indexOf(issue) === index,
        );
    }, [
        spaceError,
        errorMsg,
        conflictError,
        mustCompleteMeetingRoomDetails,
        guestCount,
        adultCount,
        spaces,
        staffAttending,
        externalAttendees,
        loggedInUser.role,
        getIndividualAsset?.type,
        getIndividualAsset?.details?.catering,
        getIndividualAsset?.details?.alcohol,
        getIndividualAsset?.details?.massages,
        getIndividualAsset?.workshopContractors?.length,
        getIndividualAsset?.otherContractors,
        requiresCatering,
        requiresBeverages,
        requiresMassages,
        customServices,
        cateringDetails,
        beverageDetails,
        massageDetails,
        optionalServiceJobsKey,
        endDate,
        formErrors,
        cannotSavePastPendingReservation,
        formJobs,
    ]);

    const validationIssueMessages = (() => {
        const fe = (formErrors ?? {}) as any;
        const { jobs: _jobsErrors, ...rest } = fe ?? {};
        return collectRhfErrorMessages(rest);
    })();

    const combinedReviewIssues = useMemo(() => {
        const next = new Set<string>([
            ...formIssues,
            ...validationIssueMessages,
        ]);
        return Array.from(next);
    }, [formIssues, validationIssueMessages]);

    const hasIssuesSection = combinedReviewIssues.length > 0;

    const issueCount = combinedReviewIssues.length;

    const disableSave =
        errors.length > 0 ||
        bookingCapError ||
        disableSaveOffice ||
        spaceError ||
        cannotSavePastPendingReservation ||
        hasIssuesSection;

    useEffect(() => {}, [
        guestCount,
        adultCount,
        childCount,
        overnightGuestCount,
        spaceType,
        isMeetingRoom,
        requiresCatering,
        requiresBeverages,
        requiresMassages,
        cateringDetails,
        beverageDetails,
        massageDetails,
        optionalServiceJobsKey,
        getIndividualAsset?.id,
        reservationDetails?.bookerType,
        trigger,
    ]);

    useEffect(() => {
        if (getIndividualAsset?.type !== ExperienceType.Commercial) return;
        void trigger(['staffAttending', 'externalAttendees'] as any);
    }, [
        getIndividualAsset?.type,
        staffAttending,
        externalAttendees,
        guestCount,
        spaceType,
        isMeetingRoom,
        trigger,
    ]);
    useEffect(() => {
        return;
    }, [
        additionalNotes,
        adultCount,
        beverageDetails,
        cateringDetails,
        checkinTime,
        checkoutTime,
        childCount,
        control,
        endDate,
        guestCount,
        isMeetingRoom,
        massageDetails,
        moduleType,
        overnightGuestCount,
        requiresBeverages,
        requiresCatering,
        requiresMassages,
        reservationDetails?.module?.id,
        reservationDetails?.policies,
        reservationDetails?.bookerType,
        reservationDetails?.onBehalf,
        getIndividualAsset?.type,
        spaceType,
        spaces,
        startDate,
        visitType,
        calculateFees,
    ]);

    const navItems: {
        key: EditNavKey;
        label: string;
        mobileLabel?: string;
        visible: boolean;
    }[] = [
        { key: 'details', label: 'Reservation', visible: true },
        {
            key: 'services',
            label: 'Additional Services',
            mobileLabel: 'Additional Services',
            visible: showOptionalServicesSection,
        },
        { key: 'notes', label: 'Notes', visible: true },
        {
            key: 'issues',
            label: 'Review issues',
            mobileLabel: 'Issues',
            visible: hasIssuesSection,
        },
    ];

    const firstVisibleNav = useMemo(() => {
        return navItems.find((n) => n.visible)?.key ?? 'details';
    }, [showOptionalServicesSection, hasIssuesSection]);

    const [activeNav, setActiveNav] = useState<EditNavKey>(firstVisibleNav);

    useEffect(() => {
        const requestedNav = (
            location.state as { activeNav?: EditNavKey } | null
        )?.activeNav;

        if (requestedNav === 'services' && showOptionalServicesSection) {
            setActiveNav('services');
        }
    }, [location.state, showOptionalServicesSection]);

    useEffect(() => {
        const isVisible = navItems.some(
            (n) => n.key === activeNav && n.visible,
        );

        if (!isVisible) {
            setActiveNav(firstVisibleNav);
        }
    }, [activeNav, firstVisibleNav, navItems]);

    if (!reservationDetails) return null;

    const renderSummarySidebarPaper = (opts?: {
        hideReservationSummary?: boolean;
    }) => {
        const hideReservationSummary = opts?.hideReservationSummary ?? false;
        const showChargesBlock = showCharges;
        const showIssuesPreview =
            combinedReviewIssues.length > 0 && activeNav !== 'issues';
        const chargesDividerSx =
            !hideReservationSummary && showChargesBlock
                ? {
                      mt: 2,
                      pt: 2,
                      borderTop: '1px solid #E2E8F0',
                  }
                : {};
        const issuesDividerSx =
            showIssuesPreview && (!hideReservationSummary || showChargesBlock)
                ? {
                      mt: 2,
                      pt: 2,
                      borderTop: `1px solid ${theme.palette.custom.lightBorderColor}`,
                  }
                : {};

        return (
            <Paper
                elevation={3}
                sx={{
                    backgroundColor: theme.palette.custom.cardActionBackground,
                    overflow: 'hidden',
                    position: { xs: 'static', md: 'sticky' },
                    top: { md: 12 },
                    zIndex: 4,
                    px: 2.25,
                    pt: hideReservationSummary ? 1.5 : 2,
                    pb: 1.75,
                }}
            >
                {!hideReservationSummary ? (
                    <ReservationSummaryBlock
                        getReservationDetails={reservationDetails}
                        getIndividualAsset={getIndividualAsset}
                        loggedInUser={loggedInUser}
                        startDate={startDate}
                        endDate={endDate}
                    />
                ) : null}

                {showChargesBlock ? (
                    <Box sx={chargesDividerSx}>
                        <ReservationChargesCard
                            show={showCharges}
                            fees={fees}
                            isCalculating={isCalculating}
                            showSkeleton={showSkeleton}
                            isFirstRender={isFirstRender}
                            reservationDetails={reservationDetails}
                            module={getIndividualAsset}
                            formJobs={formJobs}
                        />
                    </Box>
                ) : null}

                {showIssuesPreview ? (
                    <Box sx={issuesDividerSx}>
                        <Alert
                            severity='error'
                            sx={{
                                borderRadius: 2,
                                alignItems: 'flex-start',
                                '& .MuiAlert-message': {
                                    width: '100%',
                                },
                            }}
                        >
                            <Stack spacing={1.25}>
                                <Box>
                                    <Typography
                                        sx={{
                                            fontWeight: 800,
                                            fontSize: 13,
                                            mb: 0.75,
                                        }}
                                    >
                                        Fix these before saving
                                    </Typography>
                                    <Box
                                        component='ul'
                                        sx={{
                                            m: 0,
                                            pl: 2,
                                            maxHeight: 200,
                                            overflowY: 'auto',
                                            pr: 0.5,
                                            '& li': {
                                                mb: 0.35,
                                            },
                                        }}
                                    >
                                        {combinedReviewIssues.map(
                                            (issue, i) => (
                                                <Typography
                                                    key={`${i}-${issue}`}
                                                    component='li'
                                                    variant='body3'
                                                    sx={{
                                                        lineHeight: 1.5,
                                                        fontSize: 12.5,
                                                        wordBreak: 'break-word',
                                                    }}
                                                >
                                                    {issue}
                                                </Typography>
                                            ),
                                        )}
                                    </Box>
                                </Box>
                                {navItems.some(
                                    (n) => n.key === 'issues' && n.visible,
                                ) ? (
                                    <Button
                                        size='small'
                                        variant='outlined'
                                        color='error'
                                        fullWidth
                                        onClick={() => setActiveNav('issues')}
                                        sx={{
                                            textTransform: 'none',
                                            fontWeight: 700,
                                            fontSize: 12,
                                            borderRadius: 1.5,
                                            borderWidth: 1,
                                            bgcolor: (t) =>
                                                alpha(
                                                    t.palette.error.main,
                                                    0.06,
                                                ),
                                            '&:hover': {
                                                borderWidth: 1,
                                                bgcolor: (t) =>
                                                    alpha(
                                                        t.palette.error.main,
                                                        0.1,
                                                    ),
                                            },
                                        }}
                                    >
                                        Review issues
                                    </Button>
                                ) : null}
                            </Stack>
                        </Alert>
                    </Box>
                ) : null}
            </Paper>
        );
    };

    return (
        <FormProvider {...methods}>
            <form onSubmit={handleSubmit(onSubmit)}>
                {isFetching ? (
                    <Grid
                        sx={{
                            height: isLaptop
                                ? '50vh'
                                : isMobile
                                  ? '5px'
                                  : '50vh',
                            width: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            alignContent: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        <LoadingComponent />
                    </Grid>
                ) : (
                    <>
                        <Container
                            maxWidth={isOwner ? 'xl' : 'lg'}
                            sx={{
                                px: { xs: 0.5, sm: 2, md: 0 },
                                pb: { xs: 14, sm: 15 },
                                pt: { xs: 1, sm: 2 },
                            }}
                        >
                            {isMobile ? (
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: {
                                            xs: 'flex-start',
                                            sm: 'center',
                                        },
                                        justifyContent: 'space-between',
                                        gap: 2,
                                        flexWrap: 'wrap',
                                        mb: { xs: 1, sm: 2 },
                                    }}
                                >
                                    <Box sx={{ minWidth: 0 }}>
                                        <Typography
                                            variant='h1'
                                            sx={{
                                                textAlign: 'left',
                                                textTransform: 'uppercase',
                                                fontSize: {
                                                    xs: '20px',
                                                    sm: '22px',
                                                },
                                                lineHeight: 1.2,
                                                m: 0,
                                                pr: 1,
                                            }}
                                        >
                                            {experienceName}
                                        </Typography>

                                        <Box
                                            sx={{
                                                display: 'flex',
                                                gap: 1,
                                                flexWrap: 'wrap',
                                                mt: 0.75,
                                            }}
                                        >
                                            <Typography
                                                sx={{
                                                    fontSize: 12,
                                                    color: theme.palette.text
                                                        .secondary,
                                                }}
                                            >
                                                Reservation #
                                                {reservationDetails?.id}
                                            </Typography>

                                            <Typography
                                                sx={{
                                                    fontSize: 12,
                                                    color: theme.palette.text
                                                        .secondary,
                                                }}
                                            >
                                                •
                                            </Typography>

                                            <Typography
                                                sx={{
                                                    fontSize: 12,
                                                    color: theme.palette.text
                                                        .secondary,
                                                }}
                                            >
                                                Created {createdAtDate}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Box>
                            ) : null}

                            <Grid container spacing={2} alignItems='flex-start'>
                                <Grid
                                    item
                                    xs={12}
                                    md={8}
                                    lg={8.5}
                                    sx={{ order: { md: 0 } }}
                                >
                                    {!isMobile ? (
                                        <>
                                            <ReservationHeader
                                                isMobile={isMobile}
                                                isIpad={isIpad}
                                                theme={theme}
                                                reservationDetails={
                                                    reservationDetails
                                                }
                                                loggedInUser={loggedInUser}
                                                getIndividualAsset={
                                                    getIndividualAsset
                                                }
                                                createdAtDate={createdAtDate}
                                                dateDisplay={dateDisplay}
                                                onClickOpenModal={
                                                    onClickOpenModal
                                                }
                                                control={control}
                                                checkinTime={checkinTime}
                                                checkoutTime={checkoutTime}
                                                compareCheckinSettings={
                                                    compareCheckinSettings
                                                }
                                                compareCheckoutSettings={
                                                    compareCheckoutSettings
                                                }
                                                attendeesOnlyEditMode={
                                                    isApprovedActiveReservation
                                                }
                                            />

                                            {isApprovedActiveReservation && (
                                                <Alert
                                                    severity='warning'
                                                    icon={
                                                        <WarningAmberRoundedIcon fontSize='inherit' />
                                                    }
                                                    sx={{
                                                        mb: 2,
                                                        borderRadius: 2,
                                                        alignItems:
                                                            'flex-start',
                                                        '& .MuiAlert-message': {
                                                            ...theme.typography
                                                                .body3,
                                                        },
                                                    }}
                                                >
                                                    {(() => {
                                                        const rawOnBehalf =
                                                            reservationDetails?.onBehalf;
                                                        const isOnBehalfTrue =
                                                            rawOnBehalf ===
                                                                true ||
                                                            rawOnBehalf ===
                                                                'true' ||
                                                            rawOnBehalf === 1 ||
                                                            rawOnBehalf === '1';
                                                        const isOwnerBlocked =
                                                            loggedInUser?.role?.includes(
                                                                'owner',
                                                            ) &&
                                                            reservationDetails?.bookerType ===
                                                                'owner' &&
                                                            !isOnBehalfTrue;

                                                        if (isOwnerBlocked) {
                                                            return `This reservation is approved and currently inprogress, you will be unable to edit any information relating to this reservation'`;
                                                        }

                                                        return (
                                                            <>
                                                                This reservation
                                                                is approved and
                                                                currently in
                                                                progress. You
                                                                can update{' '}
                                                                <strong>
                                                                    guest
                                                                    attendees
                                                                </strong>{' '}
                                                                and{' '}
                                                                <strong>
                                                                    staff
                                                                    attending
                                                                </strong>{' '}
                                                                and manage
                                                                optional
                                                                services; all
                                                                other details
                                                                are locked until
                                                                the reservation
                                                                ends.
                                                            </>
                                                        );
                                                    })()}
                                                </Alert>
                                            )}
                                        </>
                                    ) : null}

                                    {isMobile &&
                                    assetImageSrc &&
                                    isModuleAvailable ? (
                                        <Box
                                            sx={{
                                                mb: { xs: 1, sm: 2 },
                                                borderRadius: 2,
                                                overflow: 'hidden',
                                                border: `1px solid ${theme.palette.custom.lightBorderColor}`,
                                                backgroundColor:
                                                    theme.palette.neutral.main,
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    position: 'relative',
                                                    height: {
                                                        xs: 160,
                                                        sm: 200,
                                                    },
                                                    width: '100%',
                                                }}
                                            >
                                                <Box
                                                    component='img'
                                                    src={assetImageSrc}
                                                    alt={
                                                        getIndividualAsset?.name ||
                                                        reservationDetails
                                                            ?.module?.name
                                                    }
                                                    sx={{
                                                        width: '100%',
                                                        height: '100%',
                                                        objectFit: 'cover',
                                                        display: 'block',
                                                    }}
                                                />
                                                <Box
                                                    sx={{
                                                        position: 'absolute',
                                                        inset: 0,
                                                        background:
                                                            'linear-gradient(to top, rgba(0,0,0,0.45), rgba(0,0,0,0) 55%)',
                                                    }}
                                                />
                                                <Box
                                                    sx={{
                                                        position: 'absolute',
                                                        left: 16,
                                                        right: 16,
                                                        bottom: 12,
                                                        display: 'flex',
                                                        alignItems: 'flex-end',
                                                        justifyContent:
                                                            'space-between',
                                                        gap: 1.5,
                                                    }}
                                                >
                                                    <Box sx={{ minWidth: 0 }}>
                                                        <Typography
                                                            sx={{
                                                                color: '#fff',
                                                                fontWeight: 900,
                                                                fontSize: 14,
                                                                lineHeight: 1.2,
                                                                textShadow:
                                                                    '0 1px 2px rgba(0,0,0,0.35)',
                                                                whiteSpace:
                                                                    'nowrap',
                                                                overflow:
                                                                    'hidden',
                                                                textOverflow:
                                                                    'ellipsis',
                                                            }}
                                                        >
                                                            {camelCaseToTitleCase(
                                                                getIndividualAsset?.type,
                                                            )}
                                                        </Typography>
                                                        {getIndividualAsset?.suburb ? (
                                                            <Typography
                                                                sx={{
                                                                    mt: 0.25,
                                                                    color: 'rgba(255,255,255,0.9)',
                                                                    fontWeight: 700,
                                                                    fontSize: 12,
                                                                    lineHeight: 1.2,
                                                                    textShadow:
                                                                        '0 1px 2px rgba(0,0,0,0.3)',
                                                                    whiteSpace:
                                                                        'nowrap',
                                                                    overflow:
                                                                        'hidden',
                                                                    textOverflow:
                                                                        'ellipsis',
                                                                }}
                                                            >
                                                                {
                                                                    getIndividualAsset?.suburb
                                                                }
                                                                {getIndividualAsset?.state
                                                                    ? `, ${getIndividualAsset?.state}`
                                                                    : ''}
                                                            </Typography>
                                                        ) : null}
                                                    </Box>
                                                </Box>
                                            </Box>
                                        </Box>
                                    ) : null}

                                    {isMobile &&
                                    (showCharges ||
                                        (combinedReviewIssues.length > 0 &&
                                            activeNav !== 'issues')) ? (
                                        <Box sx={{ mb: { xs: 1, sm: 2 } }}>
                                            {renderSummarySidebarPaper({
                                                hideReservationSummary: true,
                                            })}
                                        </Box>
                                    ) : null}

                                    {isMobile && isApprovedActiveReservation ? (
                                        <Alert
                                            severity='warning'
                                            icon={
                                                <WarningAmberRoundedIcon fontSize='inherit' />
                                            }
                                            sx={{
                                                mb: { xs: 1, sm: 2 },
                                                borderRadius: 2,
                                                alignItems: 'flex-start',
                                                '& .MuiAlert-message': {
                                                    ...theme.typography.body3,
                                                },
                                            }}
                                        >
                                            {(() => {
                                                const rawOnBehalf =
                                                    reservationDetails?.onBehalf;
                                                const isOnBehalfTrue =
                                                    rawOnBehalf === true ||
                                                    rawOnBehalf === 'true' ||
                                                    rawOnBehalf === 1 ||
                                                    rawOnBehalf === '1';
                                                const isOwnerBlocked =
                                                    loggedInUser?.role?.includes(
                                                        'owner',
                                                    ) &&
                                                    reservationDetails?.bookerType ===
                                                        'owner' &&
                                                    !isOnBehalfTrue;

                                                if (isOwnerBlocked) {
                                                    return `This reservation is approved and currently inprogress, you will be unable to edit any information relating to this reservation'`;
                                                }

                                                return (
                                                    <>
                                                        This reservation is
                                                        approved and currently
                                                        in progress. Only{' '}
                                                        <strong>
                                                            guest attendees
                                                        </strong>{' '}
                                                        and{' '}
                                                        <strong>
                                                            staff attending
                                                        </strong>{' '}
                                                        can be changed; all
                                                        other details are locked
                                                        until the reservation
                                                        ends.
                                                    </>
                                                );
                                            })()}
                                        </Alert>
                                    ) : null}

                                    <Box
                                        sx={{
                                            position: 'sticky',
                                            top: { xs: 64, md: 12 },
                                            zIndex: 6,
                                            backgroundColor:
                                                theme.palette.neutral.main,
                                            borderRadius: 2,
                                            mb: { xs: 1, sm: 2 },
                                            py: 1,
                                        }}
                                    >
                                        <Tabs
                                            value={activeNav}
                                            onChange={(_, v) => setActiveNav(v)}
                                            variant='scrollable'
                                            scrollButtons='auto'
                                            allowScrollButtonsMobile
                                            sx={{
                                                minHeight: 40,
                                                '& .MuiTabs-indicator': {
                                                    height: 3,
                                                    borderRadius: 3,
                                                },
                                                '& .MuiTab-root': {
                                                    minHeight: 40,
                                                    px: 1.25,
                                                    textTransform: 'none',
                                                    fontWeight: 700,
                                                    fontSize: 13,
                                                    color: theme.palette.text
                                                        .secondary,
                                                },
                                                '& .MuiTab-root.Mui-selected': {
                                                    color: theme.palette.text
                                                        .primary,
                                                },
                                            }}
                                        >
                                            {navItems
                                                .filter((n) => n.visible)
                                                .map((n) => {
                                                    const baseLabel = isMobile
                                                        ? (n.mobileLabel ??
                                                          n.label)
                                                        : n.label;
                                                    const isIssuesTab =
                                                        n.key === 'issues';
                                                    const highlightIssues =
                                                        isIssuesTab &&
                                                        issueCount > 0;
                                                    return (
                                                        <Tab
                                                            key={n.key}
                                                            value={n.key}
                                                            label={
                                                                highlightIssues ? (
                                                                    <Stack
                                                                        direction='row'
                                                                        alignItems='center'
                                                                        spacing={
                                                                            0.75
                                                                        }
                                                                        component='span'
                                                                    >
                                                                        <Box
                                                                            component='span'
                                                                            sx={{
                                                                                fontWeight: 800,
                                                                            }}
                                                                        >
                                                                            {
                                                                                baseLabel
                                                                            }
                                                                        </Box>
                                                                        <Chip
                                                                            label={
                                                                                issueCount
                                                                            }
                                                                            size='small'
                                                                            color='error'
                                                                            sx={{
                                                                                height: 22,
                                                                                minWidth: 22,
                                                                                '& .MuiChip-label':
                                                                                    {
                                                                                        px: 0.65,
                                                                                        fontSize: 11,
                                                                                        fontWeight: 900,
                                                                                    },
                                                                            }}
                                                                        />
                                                                    </Stack>
                                                                ) : (
                                                                    baseLabel
                                                                )
                                                            }
                                                            sx={
                                                                highlightIssues
                                                                    ? {
                                                                          fontWeight: 800,
                                                                          color: theme
                                                                              .palette
                                                                              .error
                                                                              .main,
                                                                          borderRadius:
                                                                              '10px 10px 0 0',
                                                                          mx: 0.25,
                                                                          '&.Mui-selected':
                                                                              {
                                                                                  color: theme
                                                                                      .palette
                                                                                      .error
                                                                                      .dark,
                                                                                  fontWeight: 900,
                                                                              },
                                                                      }
                                                                    : undefined
                                                            }
                                                        />
                                                    );
                                                })}
                                        </Tabs>
                                    </Box>

                                    <Paper
                                        elevation={0}
                                        square
                                        sx={{
                                            backgroundColor:
                                                theme.palette.neutral.main,
                                            boxShadow: 'none',
                                            borderRadius: 2,
                                            mb: { xs: 1, sm: 2 },
                                            overflow: 'hidden',
                                        }}
                                    >
                                        <ReservationEditDetailsSection
                                            activeNav={activeNav}
                                            reviewIssues={combinedReviewIssues}
                                            reservationDetails={
                                                reservationDetails
                                            }
                                            loggedInUser={loggedInUser}
                                            getIndividualAsset={
                                                getIndividualAsset
                                            }
                                            jobs={jobs}
                                            guestUsers={guestUsers}
                                            isFetchingGuestUsers={
                                                isFetchingGuestUsers
                                            }
                                            availability={availability}
                                            conflictError={conflictError}
                                            errorMsg={errorMsg}
                                            compareCheckinSettings={
                                                compareCheckinSettings
                                            }
                                            compareCheckoutSettings={
                                                compareCheckoutSettings
                                            }
                                            isOnBehalf={isOnBehalf}
                                            onClickOpenDateModal={
                                                onClickOpenModal
                                            }
                                            dateDisplay={dateDisplay}
                                            canEditDate={
                                                !isApprovedActiveReservation &&
                                                !(
                                                    reservationDetails?.status ===
                                                        'approved' &&
                                                    !loggedInUser?.role.includes(
                                                        'owner',
                                                    )
                                                )
                                            }
                                            setShouldCancelCatering={
                                                setShouldCancelServices
                                            }
                                            shouldCancelCatering={
                                                shouldCancelServices
                                            }
                                            attendeesOnlyEditMode={
                                                isApprovedActiveReservation
                                            }
                                            spaceError={spaceError}
                                            setSpaceError={setSpaceError}
                                            incompleteJobs={incompleteJobs}
                                        />
                                    </Paper>
                                </Grid>

                                <Grid
                                    item
                                    xs={12}
                                    md={4}
                                    lg={3.5}
                                    sx={{
                                        display: { xs: 'none', md: 'block' },
                                        mt: { md: 8, lg: 8.5 },
                                    }}
                                >
                                    {renderSummarySidebarPaper()}
                                </Grid>
                            </Grid>
                        </Container>

                        {!isFetching && !isItineraryItemForm ? (
                            <Box
                                sx={{
                                    position: 'fixed',
                                    left: 'var(--owner-drawer-width, 0px)',
                                    right: 0,
                                    bottom: 0,
                                    zIndex: theme.zIndex.drawer + 1,
                                    transition: 'left 0.2s ease',
                                    px: { xs: 1, sm: 2, md: 3 },
                                    pb: {
                                        xs: 'max(8px, env(safe-area-inset-bottom, 0px))',
                                        sm: 'max(12px, env(safe-area-inset-bottom, 0px))',
                                    },
                                    pt: 0,
                                    pointerEvents: 'none',
                                }}
                            >
                                <Box
                                    role='region'
                                    aria-label='Reservation editing actions'
                                    sx={{
                                        maxWidth: isOwner ? '1536px' : '1200px',
                                        mx: 'auto',
                                        borderRadius: 2,
                                        border: `1px solid ${theme.palette.yellow.main}`,
                                        backgroundColor: lighten(
                                            theme.palette.yellow.main,
                                            0.78,
                                        ),
                                        boxShadow: 6,
                                        px: { xs: 1.25, sm: 2 },
                                        py: 1.25,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        gap: 1.5,
                                        flexWrap: 'wrap',
                                        pointerEvents: 'auto',
                                    }}
                                >
                                    <Stack
                                        direction='row'
                                        spacing={1}
                                        alignItems='center'
                                        sx={{ minWidth: 0 }}
                                    >
                                        <WarningAmberRoundedIcon
                                            sx={{
                                                color: darken(
                                                    theme.palette.yellow.main,
                                                    0.2,
                                                ),
                                                fontSize: 18,
                                                flexShrink: 0,
                                            }}
                                        />

                                        <Box sx={{ minWidth: 0 }}>
                                            <Typography
                                                sx={{
                                                    fontSize: 13,
                                                    fontWeight: 800,
                                                    color: darken(
                                                        theme.palette.yellow
                                                            .main,
                                                        0.35,
                                                    ),
                                                    lineHeight: 1.2,
                                                }}
                                            >
                                                {isDirty
                                                    ? 'You have unsaved changes'
                                                    : 'You are editing this reservation'}
                                            </Typography>

                                            <Typography
                                                sx={{
                                                    fontSize: 12,
                                                    fontWeight: 600,
                                                    color: darken(
                                                        theme.palette.yellow
                                                            .main,
                                                        0.35,
                                                    ),
                                                    opacity: 0.9,
                                                    lineHeight: 1.2,
                                                    mt: 0.2,
                                                }}
                                            >
                                                {isDirty
                                                    ? 'Save your updates before leaving this page.'
                                                    : 'Changes will not be saved until you click Save.'}
                                            </Typography>
                                        </Box>
                                    </Stack>

                                    <Stack
                                        direction='row'
                                        spacing={1}
                                        sx={{
                                            width: { xs: '100%', sm: 'auto' },
                                            justifyContent: 'flex-end',
                                        }}
                                    >
                                        <CustomButton
                                            variant='outlined'
                                            color='inherit'
                                            onClick={onClickCancelEditing}
                                            sx={{
                                                minWidth: 110,
                                                backgroundColor:
                                                    theme.palette.common.white,
                                            }}
                                        >
                                            Discard
                                        </CustomButton>

                                        <CustomButton
                                            variant='contained'
                                            color='primary'
                                            type='submit'
                                            disabled={
                                                disableSave ||
                                                !isDirty ||
                                                loggedInUser?.viewOnly
                                            }
                                            sx={{ minWidth: 110 }}
                                        >
                                            Save
                                        </CustomButton>
                                    </Stack>
                                </Box>
                            </Box>
                        ) : null}
                        <CalendarModal
                            open={open}
                            onClose={handleCloseModal}
                            onSubmit={onSubmitDate}
                            data={getIndividualAsset}
                            reservation={reservationDetails}
                        />
                        <CancelModal
                            open={navigationBlockerOpen}
                            onClose={handleCancelNavigation}
                            onSubmit={handleProceedNavigation}
                        />
                        <Dialog
                            open={confirmAmountOpen}
                            onClose={() => {
                                setConfirmAmountOpen(false);
                                pendingSubmitRef.current = null;
                            }}
                            maxWidth='xs'
                            fullWidth
                        >
                            <DialogTitle>
                                <Box
                                    sx={{
                                        minWidth: 0,
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    Charges updated
                                </Box>
                                <IconButton
                                    onClick={() => {
                                        setConfirmAmountOpen(false);
                                        pendingSubmitRef.current = null;
                                    }}
                                    sx={{ color: 'text.primary' }}
                                    aria-label='close'
                                >
                                    <Close />
                                </IconButton>
                            </DialogTitle>
                            <DialogContent>
                                <Typography variant='body3'>
                                    The reservation amount has changed based on
                                    your edits. Do you want to proceed and save?
                                </Typography>
                            </DialogContent>
                            <DialogActions sx={{ px: 3, pb: 2 }}>
                                <Button
                                    variant='outlined'
                                    color='inherit'
                                    onClick={() => {
                                        setConfirmAmountOpen(false);
                                        pendingSubmitRef.current = null;
                                    }}
                                >
                                    Discard
                                </Button>
                                <Button
                                    variant='contained'
                                    color='primary'
                                    onClick={async () => {
                                        const pending =
                                            pendingSubmitRef.current;
                                        setConfirmAmountOpen(false);
                                        pendingSubmitRef.current = null;
                                        if (!pending) return;
                                        bypassConfirmRef.current = true;
                                        await submitToApi(pending);
                                    }}
                                >
                                    Proceed
                                </Button>
                            </DialogActions>
                        </Dialog>
                    </>
                )}
            </form>
        </FormProvider>
    );
}
